import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery, ApolloClient } from '@apollo/client';

import { GET_PRINT_PROVIDERS } from '../../../../api/queries';

import { TableDocumentList, Flexbox } from '@ibinder/ui-common-core';

import { StyledBreadcrumb } from './ProvidersList.styles';
import { PrintProvidersListItem } from '../PrintProviders.types';
import { COUNTRY_LIST } from '../../../../constants/common';
import { TableWrapper } from '../../../../styles/common';

import { getDefaultComparer } from '../../../../helpers/tableDocumentListHelper';

interface Props {
  restClient: ApolloClient<object>;
}

export const ProvidersList = ({ restClient }: Props) => {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<{
    field: string;
    desc: boolean;
  }>({
    field: 'name',
    desc: false
  });

  const { data, loading } = useQuery(GET_PRINT_PROVIDERS, {
    client: restClient,
    fetchPolicy: 'no-cache'
  });

  const headers: {
    id: string;
    name: string;
    propName: keyof PrintProvidersListItem;
    Cell?: (tableData: any) => any;
  }[] = [
    {
      id: '0',
      name: 'Name',
      propName: 'name',
      Cell: ({ cell }) => cell.value
    },
    {
      id: '1',
      name: 'Country',
      propName: 'country',
      Cell: ({ cell }) => COUNTRY_LIST[cell.value]?.name ?? ''
    },
    {
      id: '2',
      name: 'Ordering System',
      propName: 'orderingSystems',
      Cell: ({ cell }) => {
        return !cell?.value?.length ? (
          ''
        ) : (
          <Flexbox flexDirection="row">
            {cell?.value.map((v) => {
              return <StyledBreadcrumb status={'active'} content={v.id} />;
            })}
          </Flexbox>
        );
      }
    },
    {
      id: '3',
      name: 'Status',
      propName: 'isEnabled',
      Cell: ({ cell }) => {
        return (
          <StyledBreadcrumb
            status={!cell?.value ? 'inactive' : 'done'}
            content={!cell?.value ? 'Disabled' : 'Enabled'}
          />
        );
      }
    }
  ];

  const onSort = (sortArgs) => {
    setSorting({
      field: headers[sortArgs.id].propName,
      desc: sortArgs.desc ?? false
    });
  };

  const onTableRowAction = (e: PrintProvidersListItem) => {
    const url = `${location.pathname}/provider/${e.printProviderId}`;
    navigate(url.replace('//', '/'));
  };

  const providers: PrintProvidersListItem[] = !data?.printProviders
    ? []
    : data.printProviders
        .map((p) => ({
          ...p,
          metadata: headers.map((h) => ({
            metadataElementId: h.id,
            value: p[h.propName],
            sortValue: p[h.propName]
          }))
        }))
        .sort((o1, o2) =>
          getDefaultComparer(o1, o2, sorting.field, sorting.desc)
        );

  return (
    <TableWrapper>
      <TableDocumentList
        setSelectedIds={null}
        selectedIds={[]}
        headers={headers}
        documentQuery={providers}
        sortBy={[
          {
            id: headers.find(({ propName }) => propName === sorting.field).id,
            desc: sorting.desc
          }
        ]}
        onSort={onSort}
        metadataElements={[]}
        rowAction={onTableRowAction}
      />
    </TableWrapper>
  );
};
